import React, { useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';

import {
  MapEditorCatalogElement,
  hasBuiltGeometry,
  isGeometryMisconfigured,
} from '../mapEditorCatalog';
import {
  WALL_LAYOUT_CARD_STEP_PX,
  WALL_LAYOUT_VISIBLE_CARDS,
  useWallLayoutStyles,
  wallLayoutDialogPaperSx,
} from './moduleTreeStyles';

interface IWallLayoutDialogProps {
  open: boolean;
  // The variants of one module: same family, direction and size, differing in how many brandable
  // spots their level was authored with. Empty closes nothing — the caller decides when to open.
  variants: MapEditorCatalogElement[];
  // What the author picked to get here, named so the question is not floating free of its room.
  moduleLabel: string;
  onClose: () => void;
  onPick: (elementId: string) => void;
}

/**
 * Names a variant by what its level actually holds.
 *
 * The counts are the build's, measured from the room level, so they cannot drift from the art the
 * way a hand-written label can. An absent `spots` block is not zero — it means the build could not
 * read the level — and one shipped corridor genuinely has no spots at all, so the two are kept
 * apart.
 *
 * @param element - The variant being described.
 * @param translate - i18next `t`.
 * @returns A short phrase for the card's second line.
 */
const describeSpotCount = (
  element: MapEditorCatalogElement,
  translate: TFunction,
): string => {
  const total = element.spots?.total;

  if (typeof total !== 'number') {
    return translate('editorMode.mapEditorSpotsUnknown', 'spots unknown');
  }

  if (total === 0) {
    return translate(
      'editorMode.mapEditorWallLayoutEmpty',
      'No spots — empty walls',
    );
  }

  // Two explicit keys rather than i18next's count suffixes, matching the rest of the app.
  return total === 1
    ? translate('editorMode.mapEditorSpotsCountSingular', '{{total}} spot', {
        total,
      })
    : translate('editorMode.mapEditorSpotsCountPlural', '{{total}} spots', {
        total,
      });
};

/**
 * The second question the picker asks: which wall layout the chosen room should have.
 *
 * Each variant is its own catalog element — a separate room level authored with its own spot set —
 * so choosing one is choosing which room to place, and nothing new goes on the wire. The step
 * exists because the facets cannot tell those elements apart: they share a family, a direction and
 * a size, and only the walls differ.
 *
 * @param props.open - Whether the dialog is showing.
 * @param props.variants - The elements to choose between.
 * @param props.moduleLabel - The module the variants belong to, shown as the subtitle.
 * @param props.onClose - Called on cancel, backdrop click or Escape.
 * @param props.onPick - Called with the chosen element id; the dialog does not close itself.
 */
const WallLayoutDialog = ({
  open,
  variants,
  moduleLabel,
  onClose,
  onPick,
}: IWallLayoutDialogProps) => {
  const { t: translate } = useTranslation();
  const styles = useWallLayoutStyles();

  const trackRef = useRef<HTMLDivElement | null>(null);

  // More variants than the dialog shows at once, so the rest are reached by scrolling. Read from
  // the variant count rather than measured, because the track is sized to exactly that many cards:
  // a fourth cannot fit however wide the window is.
  const isScrollable = variants.length > WALL_LAYOUT_VISIBLE_CARDS;

  // `scrollLeft` rather than scrollBy: the smooth easing is the track's own CSS, and assigning the
  // property lets the browser clamp at both ends — so the arrows stay live and simply stop moving
  // once there is nothing left that way, with no scroll position to mirror in state.
  const scrollByCard = useCallback((direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    track.scrollLeft += direction * WALL_LAYOUT_CARD_STEP_PX;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: wallLayoutDialogPaperSx }}
      data-testid="wall-layout-dialog"
    >
      <DialogTitle sx={styles.title}>
        <Typography sx={styles.titleText}>
          {translate(
            'editorMode.mapEditorWallLayoutTitle',
            'What is the wall layout you want?',
          )}
        </Typography>
      </DialogTitle>

      <Typography sx={styles.subtitle}>{moduleLabel}</Typography>

      <DialogContent sx={styles.content}>
        <Box sx={styles.carousel}>
          {isScrollable && (
            <IconButton
              size="small"
              sx={styles.arrow}
              onClick={() => scrollByCard(-1)}
              aria-label={translate(
                'editorMode.mapEditorWallLayoutPrev',
                'Previous layouts',
              )}
              data-testid="wall-layout-prev-btn"
            >
              <ChevronLeftOutlinedIcon />
            </IconButton>
          )}

          <Box sx={styles.track} ref={trackRef} data-testid="wall-layout-track">
            {variants.map((variant) => {
              // Same distinction the draft rows draw: art this build cannot load is worth chasing,
              // art nobody has made yet is not.
              const isMisconfigured = isGeometryMisconfigured(variant);
              const isPlaceholder =
                !isMisconfigured && !hasBuiltGeometry(variant);

              return (
                <Box
                  key={variant.id}
                  component="button"
                  type="button"
                  sx={styles.card}
                  onClick={() => onPick(variant.id)}
                  data-testid={`wall-layout-option-${variant.id}`}
                >
                  <Box sx={styles.preview}>
                    {variant.thumbnailUrl ? (
                      <Box
                        component="img"
                        src={variant.thumbnailUrl}
                        alt={variant.displayName}
                        sx={styles.previewImage}
                      />
                    ) : (
                      // The kit ships no thumbnails yet, so this is the normal state rather than a
                      // failure. It says which it is instead of showing a broken frame.
                      <Box
                        sx={styles.previewEmpty}
                        data-testid={`wall-layout-preview-missing-${variant.id}`}
                      >
                        <ImageOutlinedIcon sx={styles.previewEmptyIcon} />
                        <Typography
                          component="span"
                          sx={styles.previewEmptyText}
                        >
                          {translate(
                            'editorMode.mapEditorWallLayoutNoPreview',
                            'no preview yet',
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography sx={styles.cardTitle}>
                    {variant.displayName}
                  </Typography>
                  <Typography sx={styles.cardSpots}>
                    {describeSpotCount(variant, translate)}
                  </Typography>
                  {!!variant.description && (
                    <Typography sx={styles.cardDescription}>
                      {variant.description}
                    </Typography>
                  )}

                  {(isMisconfigured || isPlaceholder) && (
                    <Box
                      sx={[
                        styles.cardWarning,
                        isMisconfigured && styles.cardWarningSevere,
                      ]}
                      data-testid={`wall-layout-warning-${variant.id}`}
                    >
                      <WarningAmberOutlinedIcon
                        sx={{ fontSize: 14, flexShrink: 0 }}
                      />
                      <Typography component="span" sx={styles.cardWarningText}>
                        {isMisconfigured
                          ? translate(
                              'editorMode.mapEditorGeometryUnavailable',
                              'This element names a room this build cannot load — Unreal falls back to a grey placeholder',
                            )
                          : translate(
                              'editorMode.mapEditorNoGeometry',
                              'This element has no room built yet — Unreal builds a grey placeholder sized to it',
                            )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {isScrollable && (
            <IconButton
              size="small"
              sx={styles.arrow}
              onClick={() => scrollByCard(1)}
              aria-label={translate(
                'editorMode.mapEditorWallLayoutNext',
                'More layouts',
              )}
              data-testid="wall-layout-next-btn"
            >
              <ChevronRightOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} data-testid="wall-layout-cancel-btn">
          {translate('common.cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WallLayoutDialog;
